"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Activity,
  BadgeCheck,
  BarChart3,
  CalendarDays,
  Camera,
  ChevronDown,
  Clock3,
  CreditCard,
  Globe,
  Spline as ChartSpline,
  Info,
  Lock,
  Plus,
  Zap,
  ShieldCheck,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import AvatarButton from '../ui/AvatarButton';
import Tooltip from '../ui/Tooltip';
import useChartSettings from '../../hooks/useChartSettings';
import { chartSettingsDefaults } from '../../config/chartSettingsDefaults';
import {
  chartIndicatorOptions,
  getChartIndicatorLabel,
  type ChartIndicatorSettingId,
} from '../../config/chartIndicators';
import {
  readTradingSettings,
  resetTradingSettings,
  saveTradingSettings,
  ANALYTICS_ASSET_CLASS_OPTIONS,
  getRuntimeTradingSettingsDefaults,
  tradingSettingsFromFormState,
  tradingSettingsToFormState,
  type AnalyticsAssetClass,
  type TradingSettingsFormState,
} from '../../config/tradingSettingsDefaults';

import BillingPanel from '../billing/BillingPanel.tsx';
import AdminToolsPage from './AdminToolsPage';
import { getSessionForAuthenticatedRequest, updateCurrentUserDisplayName } from '../../lib/auth.ts';
import { applyTenantRequestHeaders } from '../../lib/tenantRequestContext';
import { getAccountDisplayName } from '../../lib/accountDisplay';
import { extendedTimezones } from '../../constants/timezones.ts';
import { type BillingAccessState } from '../../lib/billing.ts';
import { type AdminBillingAccessState } from '../../lib/adminTools.ts';
import { type WhiteLabelAppBranding } from '../../lib/whiteLabelBranding';
import { useAssetClassVisibility } from '../../lib/assetClassVisibility';
import { currentRuntime } from '../../config/runtime';
import { useDisplayTimezonePreference } from '../app/DisplayTimezoneProvider';

type Theme = 'light' | 'dark';

type AccountIdentity = {
  uid?: string | null;
  displayName?: string | null;
  email?: string | null;
  createdAt?: string | null;
  lastLoginAt?: string | null;
};

type AccountStatusTone = 'active' | 'trial' | 'demo' | 'past-due' | 'inactive' | 'pending' | 'unavailable';

type SettingsTabItem = {
  label: 'USER INFO' | 'PREFERENCES' | 'CHARTING SETUP' | 'BILLING' | 'ADMIN';
  icon: LucideIcon;
};

const TAB_GEOM = { width: 190, height: 42, overlap: 45 } as const;
const makeViewBox = (width: number, height: number) => `0 0 ${width} ${height}`;
const VALID_IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'];
const DISPLAY_NAME_MAX_LENGTH = 40;
const TIMEZONES = extendedTimezones;

const SETTINGS_TABS: readonly SettingsTabItem[] = [
  { label: 'USER INFO', icon: UserRound },
  { label: 'PREFERENCES', icon: Globe },
  { label: 'CHARTING SETUP', icon: BarChart3 },
  { label: 'BILLING', icon: CreditCard },
  { label: 'ADMIN', icon: ShieldCheck },
] as const;

const SettingsTab = ({ tab, isActive, onClick }: { tab: SettingsTabItem; isActive: boolean; onClick: () => void }) => {
  const Icon = tab.icon;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      className={`flex items-center gap-2 px-6 py-3 text-sm font-normal transition-all relative whitespace-nowrap outline-none ${isActive ? 'text-blue-700 dark:text-cyan-500' : 'text-gray-500 hover:text-black dark:text-gray-500 dark:hover:text-gray-300'}`}
    >
      <Icon size={16} className="shrink-0" />
      <span className="whitespace-nowrap">{tab.label}</span>
      {isActive ? (
        <>
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-cyan-500 shadow-[0_0_20px_rgba(37,99,235,0.8)] dark:shadow-[0_0_20px_rgba(59,130,246,0.8)] z-10 animate-in fade-in duration-300" />
          <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-blue-600/15 via-blue-600/5 to-transparent dark:from-cyan-500/15 dark:via-cyan-500/5 pointer-events-none animate-in slide-in-from-bottom-1 fade-in duration-500" />
        </>
      ) : null}
    </button>
  );
};

const SettingsCardHeader = ({ eyebrow, title, description }: { eyebrow: string; title: string; description?: string }) => (
  <div>
    <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 dark:text-gray-500">{eyebrow}</p>
    <h3 className="mt-3 text-2xl font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
    {description ? <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">{description}</p> : null}
  </div>
);

type ChartSettingTooltipCopy = {
  title: string;
  description: string;
  visual: string;
  result: string;
};

const ChartSettingTooltip = ({ isDark, copy }: { isDark: boolean; copy: ChartSettingTooltipCopy }) => {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  const triggerClassName = isDark
    ? 'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-500 transition-colors hover:border-sky-400/30 hover:bg-white/10 hover:text-sky-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40'
    : 'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-zinc-300 bg-white text-zinc-500 transition-colors hover:border-sky-300 hover:bg-zinc-100 hover:text-sky-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40';
  const tooltipShellClassName = isDark
    ? 'border border-slate-700/80 bg-[#0b1220] text-slate-100 shadow-[0_24px_60px_rgba(0,0,0,0.45)]'
    : 'border border-zinc-200 bg-white text-zinc-900 shadow-[0_24px_60px_rgba(15,23,42,0.12)]';
  const tooltipTitleClassName = isDark
    ? 'text-[10px] font-semibold uppercase tracking-[0.22em] text-sky-300'
    : 'text-[10px] font-semibold uppercase tracking-[0.22em] text-sky-700';
  const tooltipDescriptionClassName = isDark ? 'mt-1 text-[11px] leading-5 text-slate-300' : 'mt-1 text-[11px] leading-5 text-zinc-600';
  const tooltipExampleClassName = isDark ? 'mt-2 rounded-xl border border-white/10 bg-white/5 px-2.5 py-2' : 'mt-2 rounded-xl border border-zinc-200 bg-zinc-50 px-2.5 py-2';
  const tooltipExampleTextClassName = isDark ? 'font-mono text-[11px] leading-5 text-slate-100' : 'font-mono text-[11px] leading-5 text-zinc-900';
  const tooltipResultClassName = isDark ? 'mt-1 text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400' : 'mt-1 text-[10px] font-medium uppercase tracking-[0.12em] text-zinc-500';

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        aria-label={copy.title}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className={triggerClassName}
      >
        <Info size={11} strokeWidth={2.5} />
      </button>
      <Tooltip
        anchorRef={anchorRef}
        open={open}
        side="right"
        offset={10}
        customClassName={tooltipShellClassName}
      >
        <div className="max-w-[220px] whitespace-normal">
          <div className={tooltipTitleClassName}>{copy.title}</div>
          <div className={tooltipDescriptionClassName}>{copy.description}</div>
          <div className={tooltipExampleClassName}>
            <div className={tooltipExampleTextClassName}>{copy.visual}</div>
            <div className={tooltipResultClassName}>{copy.result}</div>
          </div>
        </div>
      </Tooltip>
    </>
  );
};

const isValidImageName = (name: string) => {
  const dot = name.lastIndexOf('.');
  if (dot === -1) return false;
  const ext = name.slice(dot + 1).toLowerCase();
  return VALID_IMAGE_EXTS.includes(ext);
};

const getInitials = (name: string) => {
  const normalized = String(name || '')
    .replace(/[^a-z0-9]+/gi, ' ')
    .trim();
  if (!normalized) return 'U';
  const parts = normalized.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : parts[0]?.[1] || '';
  return (first + last).toUpperCase() || 'U';
};

const normalizeStatusValue = (value: unknown) => String(value || '').trim().toLowerCase().replace(/[_\s]+/g, ' ');

const formatMemberSince = (value?: string | null) => {
  if (!value) return 'Not available';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Not available';
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(parsed);
};

const formatRelativeTime = (value?: string | null) => {
  if (!value) return 'Not available';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Not available';

  const diffMs = Math.max(0, Date.now() - parsed.getTime());
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const month = 30 * day;
  const year = 365 * day;

  if (diffMs < minute) return 'just now';
  if (diffMs < hour) return `${Math.floor(diffMs / minute)}m ago`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h ago`;
  if (diffMs < month) return `${Math.floor(diffMs / day)}d ago`;
  if (diffMs < year) return `${Math.floor(diffMs / month)}mo ago`;
  return `${Math.floor(diffMs / year)}y ago`;
};

const formatLastLogin = (value?: string | null) => ({
  value: value ? 'Current session' : 'Not available',
  detail: value ? `Signed in ${formatRelativeTime(value)}` : 'Sign-in activity unavailable',
});

const getAccountStatusModel = (billingState?: BillingAccessState | null, billingReady = false): { label: string; detail: string; tone: AccountStatusTone } => {
  if (billingState) {
    if (billingState.isAdminBillingBypass) {
      return { label: 'DEMO', detail: 'Billing bypass access', tone: 'demo' };
    }
    const normalizedStatus = normalizeStatusValue(billingState.subscriptionStatus || billingState.status);
    const isTrial =
      normalizedStatus === 'trial' ||
      normalizedStatus === 'trialing' ||
      (normalizedStatus.startsWith('trial') && !normalizedStatus.includes('ended') && !normalizedStatus.includes('expired') && !normalizedStatus.includes('cancel'));
    const isPastDue =
      normalizedStatus === 'past due' ||
      normalizedStatus.includes('past due') ||
      normalizedStatus.includes('pastdue') ||
      normalizedStatus === 'unpaid' ||
      normalizedStatus === 'payment failed' ||
      normalizedStatus === 'incomplete';

    if (billingState.hasAccess || normalizedStatus === 'active') {
      return { label: 'Active', detail: 'Renews automatically', tone: 'active' };
    }

    if (isTrial) {
      return { label: 'Trial', detail: 'Free trial access is active', tone: 'trial' };
    }

    if (isPastDue) {
      return { label: 'Past due', detail: 'Update billing to keep access active', tone: 'past-due' };
    }

    if (normalizedStatus === 'inactive' || normalizedStatus === 'canceled' || normalizedStatus === 'cancelled') {
      return { label: 'Inactive', detail: 'No active subscription is attached', tone: 'inactive' };
    }

    return { label: 'Unavailable', detail: 'Billing status is unavailable', tone: 'unavailable' };
  }

  if (!billingReady) {
    return { label: 'Checking', detail: 'Billing status is loading', tone: 'pending' };
  }

  return { label: 'Unavailable', detail: 'Billing status is unavailable', tone: 'unavailable' };
};

const PlaceholderContent = ({ tabName, avatarImage, initials }: { tabName: string; avatarImage?: string | null; initials: string }) => (
  <div className="mt-8 py-20 text-center text-zinc-500 dark:text-zinc-400">
    <div className="mb-6 flex flex-col items-center gap-3">
      <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border border-zinc-200 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900">
        {avatarImage ? <img src={avatarImage} alt="Your avatar" className="h-full w-full object-cover" /> : <span className="text-lg font-bold text-blue-500">{initials}</span>}
      </div>
      <p className="text-base font-medium text-zinc-700 dark:text-zinc-200">{tabName}</p>
    </div>
    <p>This section is still being refined.</p>
  </div>
);

const UserInfoContent = ({
  accountIdentity,
  avatarImage,
  openAvatarPicker,
  onOpenDisplayNameModal,
  onAvatarDrop,
  onAvatarKeyDown,
  onRemoveAvatar,
  avatarError,
  billingState,
  billingReady,
  isDark,
}: {
  accountIdentity?: AccountIdentity | null;
  avatarImage: string | null;
  openAvatarPicker: () => void;
  onOpenDisplayNameModal: () => void;
  onAvatarDrop: (event: React.DragEvent<HTMLButtonElement>) => void;
  onAvatarKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  onRemoveAvatar: () => void;
  avatarError: string | null;
  billingState?: BillingAccessState | null;
  billingReady?: boolean;
  isDark: boolean;
}) => {
  const accountDisplayName = String(accountIdentity?.displayName || '').trim();
  const accountEmail = String(accountIdentity?.email || '').trim();
  // `monogramInitial` is a single uppercase letter used for the monogram
  const monogramInitial = (String(accountDisplayName || accountEmail.split('@')[0] || '').trim().charAt(0) || 'U').toUpperCase();
  const displayLabel = getAccountDisplayName(accountDisplayName, accountEmail) || 'Not set';
  const emailLabel = accountEmail || 'No email on file';
  const memberSince = formatMemberSince(accountIdentity?.createdAt);
  const lastLogin = formatLastLogin(accountIdentity?.lastLoginAt);
  const statusModel = getAccountStatusModel(billingState, billingReady);

  const cardClassName = isDark
    ? 'flex h-full flex-col overflow-hidden rounded-lg border border-slate-800/80 bg-[#0b0f14] text-white shadow-[0_24px_90px_rgba(0,0,0,0.45)]'
    : 'flex h-full flex-col overflow-hidden rounded-lg border border-zinc-200/80 bg-white text-zinc-900 shadow-[0_20px_50px_rgba(15,23,42,0.08)]';
  const headerClassName = isDark
    ? 'border-b border-slate-800/60 bg-gradient-to-r from-slate-900/60 to-transparent px-6 pb-6 pt-5 md:px-8 md:pb-8 md:pt-6'
    : 'border-b border-zinc-200/70 bg-gradient-to-r from-zinc-50 to-transparent px-6 pb-6 pt-5 md:px-8 md:pb-8 md:pt-6';
  const bodyClassName = 'flex flex-1 flex-col p-6 md:p-8';
  const footClassName = isDark
    ? 'border-t border-slate-800/50 bg-[#090d12]/72 px-6 py-3.5 md:px-8'
    : 'border-t border-zinc-200/70 bg-zinc-50/80 px-6 py-3.5 md:px-8';
  const primaryButtonClassName = isDark
    ? 'flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white shadow-lg shadow-blue-900/20 transition-all hover:bg-blue-500'
    : 'flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white shadow-lg shadow-blue-900/15 transition-all hover:bg-blue-700';
  const removeButtonClassName = isDark
    ? 'rounded-xl border border-slate-700/80 bg-transparent px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-300 transition hover:border-slate-500 hover:text-white disabled:cursor-not-allowed disabled:border-slate-800 disabled:text-slate-600'
    : 'rounded-xl border border-zinc-200 bg-white px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-600 transition hover:border-zinc-300 hover:text-zinc-900 disabled:cursor-not-allowed disabled:border-zinc-200 disabled:text-zinc-400';

  const identityEyebrowClassName = isDark
    ? 'mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500'
    : 'mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500';
  const statLabelClassName = isDark
    ? 'mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gray-500'
    : 'mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500';
  const statValueClassName = isDark ? 'text-lg font-bold text-white' : 'text-lg font-bold text-zinc-950';
  const statDetailClassName = isDark ? 'text-[10px] text-gray-500' : 'text-[10px] text-zinc-500';
  const statusIndicatorClassName = statusModel.tone === 'active'
    ? 'h-2 w-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)] animate-pulse'
    : statusModel.tone === 'trial'
      ? 'h-2 w-2 rounded-full bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.7)] animate-pulse'
      : statusModel.tone === 'demo'
        ? 'h-2 w-2 rounded-full bg-cyan-500 shadow-[0_0_8px_rgba(6,182,212,0.8)] animate-pulse'
        : 'h-2 w-2 rounded-full bg-slate-400';
  const statusDetailClassName = statusModel.tone === 'active'
    ? 'text-[10px] font-medium text-emerald-500/70'
    : statusModel.tone === 'trial'
      ? 'text-[10px] font-medium text-amber-500/70'
      : statusModel.tone === 'demo'
        ? 'text-[10px] font-medium text-cyan-500/70'
        : 'text-[10px] font-medium text-gray-500';
  const noticeClassName = isDark
    ? 'text-xs italic leading-relaxed text-gray-500/90'
    : 'text-xs italic leading-relaxed text-zinc-500/90';
  const noticeIconClassName = isDark ? 'text-blue-500/65' : 'text-blue-600/60';

  return (
    <section className={cardClassName}>
      <div className={headerClassName}>
        <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
          <div className="flex items-center gap-6">
            <div className="relative">
              <div className="md:scale-100 scale-[0.83]">
                <AvatarButton
                  onClick={openAvatarPicker}
                  onKeyDown={onAvatarKeyDown}
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'copy';
                  }}
                  onDrop={onAvatarDrop}
                  ariaLabel="Change avatar"
                  avatarUrl={avatarImage}
                  name={displayLabel}
                  initial={monogramInitial}
                  theme={isDark ? 'dark' : 'light'}
                  diameter={96}
                  showChevron={false}
                />
              </div>
            </div>

            <div className="min-w-0">
              <div className={identityEyebrowClassName}>Account Identity</div>
              <div className="mb-1 flex min-w-0 items-center gap-2">
                <h2 className={`truncate text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-zinc-950'}`}>{displayLabel}</h2>
                <BadgeCheck size={20} className="shrink-0 text-blue-500" />
              </div>
              <p className={`truncate text-sm ${isDark ? 'text-gray-400' : 'text-zinc-500'}`}>{emailLabel}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-start gap-3">
            <button type="button" onClick={onOpenDisplayNameModal} className={removeButtonClassName}>
              Edit name
            </button>

            <div className="ml-auto flex items-center gap-3">
              <button type="button" onClick={openAvatarPicker} className={primaryButtonClassName}>
                <Camera size={14} />
                Change Avatar
              </button>
              {avatarImage ? (
                <button type="button" onClick={onRemoveAvatar} className={removeButtonClassName}>
                  Remove Avatar
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className={bodyClassName}>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          <div className="space-y-1">
            <div className={statLabelClassName}>
              <CalendarDays size={12} className="text-blue-500/50" />
              Member since
            </div>
            <div className={statValueClassName}>{memberSince}</div>
          </div>

          <div className="space-y-1">
            <div className={statLabelClassName}>
              <Activity size={12} className="text-blue-500/50" />
              Account status
            </div>
            <div className="flex items-center gap-2">
              <div className={statusIndicatorClassName} />
              <span className={`text-lg font-bold uppercase tracking-tight ${isDark ? 'text-white' : 'text-zinc-950'}`}>{statusModel.label}</span>
            </div>
            <p className={statusDetailClassName}>{statusModel.detail}</p>
          </div>

          <div className="space-y-1">
            <div className={statLabelClassName}>
              <Clock3 size={12} className="text-blue-500/50" />
              Last Login
            </div>
            <div className={statValueClassName}>{lastLogin.value}</div>
            <p className={statDetailClassName}>{lastLogin.detail}</p>
          </div>
        </div>
      </div>

      <div className={footClassName}>
        <div className="flex items-center gap-3">
          <ShieldCheck size={14} className={noticeIconClassName} />
          <p className={noticeClassName}>
            Your account identity is synchronized with your global Trade‑Proof profile. For updates to your email or other account details, please contact our support team.
          </p>
        </div>
      </div>

      {avatarError ? <p className="px-6 pb-5 pt-0 text-sm text-red-500 md:px-8">{avatarError}</p> : null}
    </section>
  );
};

const SecurityCard = ({ onOpenPasswordModal, isDark }: { onOpenPasswordModal: () => void; isDark: boolean }) => {
  const cardClassName = isDark
    ? 'flex h-full flex-col overflow-hidden rounded-lg border border-slate-800/80 bg-[#0b0f14] text-white shadow-[0_24px_90px_rgba(0,0,0,0.45)]'
    : 'flex h-full flex-col overflow-hidden rounded-lg border border-zinc-200/80 bg-white text-zinc-900 shadow-[0_20px_50px_rgba(15,23,42,0.08)]';
  const headerClassName = isDark
    ? 'border-b border-slate-800/70 bg-gradient-to-r from-slate-900/60 to-transparent px-6 pb-6 pt-5 md:h-[153px] md:px-8 md:pb-8 md:pt-6'
    : 'border-b border-zinc-200/70 bg-gradient-to-r from-zinc-50 to-transparent px-6 pb-6 pt-5 md:h-[153px] md:px-8 md:pb-8 md:pt-6';
  const bodyClassName = 'flex flex-1 items-center justify-center p-6 md:p-8';
  const buttonClassName = isDark
    ? 'inline-flex w-fit items-center justify-center rounded-2xl bg-blue-600 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-white shadow-lg shadow-blue-900/20 transition hover:bg-blue-500'
    : 'inline-flex w-fit items-center justify-center rounded-2xl bg-blue-600 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-white shadow-lg shadow-blue-900/15 transition hover:bg-blue-700';
  const footClassName = isDark
    ? 'border-t border-slate-800/50 bg-[#090d12]/72 px-6 py-3.5 md:px-8'
    : 'border-t border-zinc-200/70 bg-zinc-50/80 px-6 py-3.5 md:px-8';
  const noteTextClassName = isDark ? 'text-[10px] italic leading-relaxed text-gray-500/90' : 'text-[10px] italic leading-relaxed text-zinc-500/90';
  const noteIconClassName = isDark ? 'text-blue-500/65' : 'text-blue-600/60';

  return (
    <section className={cardClassName}>
      <div className={headerClassName}>
        <SettingsCardHeader
          eyebrow="Security"
          title="Password"
          description="Update your password to keep your trading terminal protected."
        />
      </div>

      <div className={bodyClassName}>
        <button type="button" onClick={onOpenPasswordModal} className={buttonClassName}>
          Change Password
        </button>
      </div>

      <div className={footClassName}>
        <div className="flex items-center gap-3">
          <ShieldCheck size={14} className={noteIconClassName} />
          <p className={noteTextClassName}>
            Password changes are protected by your current session.
          </p>
        </div>
      </div>
    </section>
  );
};

export const TimeZoneSelect: React.FC<{ value?: string; onChange?: (v: string) => void }> = ({ value = "America/New_York", onChange }) => {
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  return (
    <div className="relative">
      <select
        value={value}
        onChange={(event) => onChange?.(event.target.value)}
        className={isDark
          ? 'w-full appearance-none rounded-xl border border-gray-800 bg-black/40 pl-4 pr-8 py-3 text-sm text-white outline-none focus:border-blue-500/50'
          : 'w-full appearance-none rounded-xl border border-zinc-300 bg-white pl-4 pr-8 py-3 text-sm text-zinc-900 outline-none focus:border-blue-500/50'}
      >
        {TIMEZONES.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown size={14} className={isDark ? 'pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-gray-500' : 'pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-zinc-500'} />
    </div>
  );
};

// Minimal SnapTrade integration card
const SnapTradeCard: React.FC<{ isDark: boolean }> = ({ isDark }) => {
  const [available, setAvailable] = React.useState<boolean | null>(null);
  const [loadingStatus, setLoadingStatus] = React.useState(false);
  const [connecting, setConnecting] = React.useState(false);
  const [accounts, setAccounts] = React.useState<Array<Record<string, unknown>> | null>(null);
  const [loadingAccounts, setLoadingAccounts] = React.useState(false);
  const [syncingAccountId, setSyncingAccountId] = React.useState<string | null>(null);
  const [syncMessage, setSyncMessage] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [availabilityError, setAvailabilityError] = React.useState<string | null>(null);

  const getSnapTradeHeaders = useCallback(async () => {
    const headers: Record<string, string> = {};
    applyTenantRequestHeaders(headers);
    const session = await getSessionForAuthenticatedRequest({ reason: 'snaptrade_connect_button' });
    const accessToken = String(session?.access_token || '').trim();
    if (!accessToken) throw new Error('missing_access_token');
    headers.Authorization = `Bearer ${accessToken}`;

    return headers;
  }, []);

  const safeAccount = useCallback((a: unknown): Record<string, unknown> | null => {
    if (!a || typeof a !== 'object') return null;
    const obj = a as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    Object.keys(obj).forEach((k) => {
      const lk = String(k).toLowerCase();
      if (lk.includes('secret') || lk.includes('token') || lk.includes('key') || lk.includes('password')) return;
      out[k] = obj[k];
    });
    return out;
  }, []);

  const getAccountText = useCallback((account: Record<string, unknown>, keys: string[], fallback: string) => {
    for (const key of keys) {
      const value = key.split('.').reduce<unknown>((current, segment) => {
        if (!current || typeof current !== 'object') return undefined;
        return (current as Record<string, unknown>)[segment];
      }, account);
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) return trimmed;
      }
    }
    return fallback;
  }, []);

  const getAccountId = useCallback((account: Record<string, unknown>) => {
    return getAccountText(account, ['id', 'accountId', 'account_id'], '');
  }, [getAccountText]);

  const [lastChecked, setLastChecked] = React.useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    setLoadingStatus(true);
    setAvailabilityError(null);
    setError(null);
    try {
      const res = await fetch('/api/integrations/snaptrade/status', { method: 'GET' });
      if (!res.ok) throw new Error('status fetch failed');
      const body = await res.json() as unknown;
      let avail: boolean | undefined;
      if (body && typeof body === 'object') {
        const b = body as Record<string, unknown>;
        if (typeof b.configured === 'boolean') avail = b.configured as boolean;
        else if (typeof b.available === 'boolean') avail = b.available as boolean;
        // optional last-checked field from status endpoint
        if (b.lastChecked) {
          try {
            if (typeof b.lastChecked === 'number') setLastChecked(new Date(b.lastChecked as number).toLocaleString());
            else if (typeof b.lastChecked === 'string') setLastChecked(String(b.lastChecked));
          } catch {
            setLastChecked(String(b.lastChecked));
          }
        }
      }
      setAvailable(!!avail);
    } catch {
      setAvailable(false);
      setAvailabilityError('Unable to determine SnapTrade availability.');
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  const fetchAccounts = useCallback(async () => {
    if (available !== true) {
      setAccounts([]);
      setLoadingAccounts(false);
      return;
    }
    setLoadingAccounts(true);
    setError(null);
    try {
      const headers = await getSnapTradeHeaders();
      const res = await fetch('/api/integrations/snaptrade/accounts', { method: 'GET', headers });
      if (!res.ok) throw new Error('accounts fetch failed');
      const body = await res.json() as unknown;
      let list: Array<Record<string, unknown>> = [];
      if (body && typeof body === 'object') {
        const b = body as Record<string, unknown>;
        if (Array.isArray(b.accounts)) {
          list = (b.accounts as unknown[]).map(safeAccount).filter(Boolean) as Array<Record<string, unknown>>;
        }
      }
      setAccounts(list);
    } catch (accountsError) {
      setAccounts([]);
      setError(accountsError instanceof Error && ['missing_access_token', 'no session'].includes(accountsError.message)
        ? 'Sign in to view linked accounts.'
        : 'Unable to fetch linked accounts.');
    } finally {
      setLoadingAccounts(false);
    }
  }, [available, getSnapTradeHeaders, safeAccount]);

  const connectedBrokerLabel = React.useMemo(() => {
    if (!Array.isArray(accounts) || accounts.length === 0) return 'Not connected.';

    const brokerNames = accounts
      .map((account) => getAccountText(account, [
        'institution_name',
        'institutionName',
        'institution.name',
        'broker',
        'broker_name',
        'brokerName',
        'brokerage_name',
        'brokerageName',
        'brokerage.name',
        'provider',
      ], ''))
      .filter(Boolean);

    const uniqueBrokerNames = Array.from(new Set(brokerNames));
    return uniqueBrokerNames.length > 0 ? uniqueBrokerNames.join(', ') : 'Connected broker';
  }, [accounts, getAccountText]);
  const hasConnectedBrokerAccount = Array.isArray(accounts) && accounts.length > 0;
  const brokerConnectionStatus = loadingStatus || loadingAccounts
    ? 'Checking...'
    : !available
      ? 'Unavailable'
      : hasConnectedBrokerAccount
        ? 'Connected'
        : 'Awaiting broker connection';

  const handleSyncTrades = useCallback(async (account: Record<string, unknown>) => {
    const accountId = getAccountId(account);
    const accountName = getAccountText(account, ['name', 'accountName', 'id'], 'SnapTrade account');

    if (!accountId) {
      setError('Unable to identify the selected broker account.');
      return;
    }

    setSyncingAccountId(accountId);
    setError(null);
    setSyncMessage(null);

    try {
      const session = await getSessionForAuthenticatedRequest({ reason: 'snaptrade_sync_button' });
      const accessToken = String(session?.access_token || '').trim();
      if (!accessToken) {
        setError('Sign in to sync broker trades.');
        return;
      }

      const res = await fetch('/api/integrations/snaptrade/sync', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accountId, daysBack: 30 }),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(String((body as Record<string, unknown>)?.error || 'Unable to sync broker trades.'));
      }

      const syncResult = body && typeof body === 'object' ? (body as Record<string, unknown>).sync as Record<string, unknown> | undefined : undefined;
      const importedTradeCount = Number(syncResult?.importedTradeCount ?? 0);
      setSyncMessage(`Synced ${importedTradeCount} trade${importedTradeCount === 1 ? '' : 's'} from ${accountName}.`);
      await fetchAccounts();
    } catch {
      setError('Unable to sync broker trades.');
    } finally {
      setSyncingAccountId(null);
    }
  }, [fetchAccounts, getAccountId, getAccountText]);

  const handleConnect = useCallback(async () => {
    setConnecting(true);
    setError(null);
    try {
      const headers = await getSnapTradeHeaders();
      const res = await fetch('/api/integrations/snaptrade/connect', { method: 'POST', headers });
      const body = await res.json().catch(() => ({} as unknown));
      if (!res.ok) {
        const errorCode = body && typeof body === 'object' ? String((body as Record<string, unknown>).error || '') : '';
        const stage = body && typeof body === 'object' ? String((body as Record<string, unknown>).stage || '') : '';
        // Specific error codes first so real 400/401/403/404 conditions surface with
        // their accurate messages instead of being masked by the generic stage fallback.
        if (errorCode === 'snaptrade_not_configured' || errorCode === 'tenant_not_found' || errorCode === 'tenant_inactive') {
          throw new Error('Broker connection is not available for this workspace yet.');
        }
        if (errorCode === 'invalid_auth_token' || errorCode === 'unauthorized') {
          throw new Error('Sign in to connect a broker.');
        }
        if (stage === 'register user') {
          throw new Error('Failed to register user with SnapTrade.');
        }
        if (stage === 'login secret' || stage === 'connection link/session') {
          throw new Error('Failed to generate connection session.');
        }
        if (stage === 'tenant/config resolution') {
          throw new Error('Workspace configuration missing or invalid.');
        }
        if (errorCode === 'snaptrade_request_failed' || errorCode === 'SNAPTRADE_API_ERROR' || errorCode === 'request_failed') {
          throw new Error('Broker connection temporarily unavailable. Try again later.');
        }
        throw new Error('Unable to start connection. Try again later.');
      }
      let url: string | undefined;
      if (body && typeof body === 'object') {
        const b = body as Record<string, unknown>;
        if (typeof b.url === 'string') url = b.url as string;
      }
      if (url) {
        // open in a new tab/window so user can complete connection
        window.open(url, '_blank', 'noopener');
        // After user returns focus, refresh accounts
        const onFocus = () => { fetchAccounts(); window.removeEventListener('focus', onFocus); };
        window.addEventListener('focus', onFocus);
      } else {
        throw new Error('no connection URL returned');
      }
    } catch (err) {
      const message = err instanceof Error ? String(err.message || '').trim() : '';
      // Surface the real safe API error; keep the generic copy only for internal
      // sentinels (e.g. missing connection URL) and unexpected failures.
      setError(message === 'missing_access_token' || message === 'no session'
        ? 'Sign in to connect a broker.'
        : message && message !== 'no connection URL returned'
          ? message
          : 'Unable to start connection. Try again later.');
    } finally {
      setConnecting(false);
    }
  }, [fetchAccounts, getSnapTradeHeaders]);

  React.useEffect(() => {
    fetchStatus();
    fetchAccounts();
  }, [fetchAccounts, fetchStatus]);

  // provide a light-weight refresh when tab regains focus
  React.useEffect(() => {
    const onFocus = () => { fetchStatus(); fetchAccounts(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchAccounts, fetchStatus]);

  React.useEffect(() => {
    const refreshFromCallback = () => {
      fetchStatus();
      fetchAccounts();
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'tt:snaptrade:callback') return;
      refreshFromCallback();
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key !== 'tt_snaptrade_callback') return;
      refreshFromCallback();
    };

    window.addEventListener('message', onMessage);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('storage', onStorage);
    };
  }, [fetchAccounts, fetchStatus]);

  const cardClassName = isDark
    ? 'rounded-lg border border-slate-800/80 bg-[#0b0f14] p-6 text-white shadow-[0_24px_90px_rgba(0,0,0,0.45)] md:p-8'
    : 'rounded-lg border border-zinc-200 bg-white p-6 shadow-sm md:p-8';
  const titleClassName = isDark ? 'text-2xl font-bold text-white' : 'text-2xl font-bold text-zinc-950';
  const descriptionClassName = isDark ? 'mt-1 text-xs text-gray-500' : 'mt-1 text-xs text-zinc-500';
  const buttonPrimaryClassName = isDark
    ? 'px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-[11px] font-bold uppercase tracking-[0.18em] rounded-xl text-white transition-all shadow-lg shadow-blue-900/20'
    : 'px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-[11px] font-bold uppercase tracking-[0.18em] rounded-xl text-white transition-all shadow-lg shadow-blue-900/20';
  const buttonSecondaryClassName = isDark
    ? 'px-5 py-2.5 bg-gray-800/50 hover:bg-gray-800 text-[11px] font-bold uppercase tracking-[0.18em] rounded-xl text-gray-300 transition-all border border-gray-700'
    : 'px-5 py-2.5 bg-zinc-100 hover:bg-zinc-200 text-[11px] font-bold uppercase tracking-[0.18em] rounded-xl text-zinc-700 transition-all border border-zinc-300';

  const columnDividerClassName = isDark ? 'md:divide-x md:divide-slate-800/40' : 'md:divide-x md:divide-zinc-200/70';

  return (
    <section className={cardClassName}>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className={`${titleClassName} mb-1`}>Broker connection</h3>
          <p className={descriptionClassName}>Connect a broker through SnapTrade to verify linked accounts.</p>
        </div>
        <span className={`rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
          loadingStatus || available
            ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-500'
            : 'border-red-500/30 bg-red-500/10 text-red-500'
        }`}>
          {loadingStatus ? 'Checking...' : available ? 'Available' : 'Unavailable'}
        </span>
      </div>

      <div className={isDark ? 'border-t border-slate-800/40 mb-6' : 'border-t border-zinc-200 mb-6'} />

      <div className={`grid grid-cols-1 gap-6 md:grid-cols-2 ${columnDividerClassName}`}>
        <div className="md:pr-6">
          <div className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">Connection</div>
          <div className="text-sm text-gray-500">Connect your broker to enable broker-based imports.</div>

          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={(event) => {
                try { event.stopPropagation(); } catch {}
                handleConnect();
              }}
              disabled={!available || connecting}
              className={buttonPrimaryClassName}
            >
              {connecting ? 'Connecting…' : 'Connect Broker'}
            </button>
            <button
              type="button"
              onClick={fetchAccounts}
              disabled={loadingAccounts}
              className={buttonSecondaryClassName}
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="md:pl-6">
          <div className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">Account status</div>

          <div className="grid grid-cols-1 gap-2 text-sm">
            <div className="flex items-center justify-between gap-4 text-xs text-gray-400">
              <span>Broker</span>
              <span className="min-w-0 truncate text-right" title={connectedBrokerLabel}>{connectedBrokerLabel}</span>
            </div>
            <div className="flex items-center justify-between text-xs text-gray-400"><span>Linked accounts</span><span>{Array.isArray(accounts) ? accounts.length : 0}</span></div>
            <div className="flex items-center justify-between text-xs text-gray-400"><span>Last checked</span><span>{lastChecked ?? '—'}</span></div>
            <div className="flex items-center justify-between gap-4 text-xs text-gray-400">
              <span>Status</span>
              <span className="text-right">{brokerConnectionStatus}</span>
            </div>
          </div>

          <div className="mt-3">
            {syncMessage ? <p className="mb-2 text-xs text-emerald-600 dark:text-emerald-400">{syncMessage}</p> : null}
            {loadingAccounts ? (
              <p className="text-xs text-gray-500">Loading accounts…</p>
            ) : availabilityError ? (
              <p className="text-xs text-red-400">{availabilityError}</p>
            ) : available === false ? (
              <p className="text-xs text-gray-500">Broker Sync is not enabled for this workspace.</p>
            ) : error ? (
              <p className="text-xs text-red-400">{error}</p>
            ) : Array.isArray(accounts) && accounts.length ? (
              <ul className="space-y-2">
                {accounts.map((account, index) => (
                  <li key={index} className="rounded border border-zinc-100 bg-zinc-50 p-2 text-sm dark:border-zinc-700 dark:bg-zinc-800">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-medium text-zinc-800 dark:text-zinc-100">{getAccountText(account, ['name', 'accountName', 'id'], 'Account')}</div>
                        <div className="text-xs text-zinc-500">{getAccountText(account, ['broker', 'provider'], '')}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => { void handleSyncTrades(account); }}
                        disabled={syncingAccountId === getAccountId(account)}
                        className={isDark
                          ? 'rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-blue-200 transition hover:bg-blue-500/20 disabled:cursor-not-allowed disabled:opacity-60'
                          : 'rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-blue-700 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60'}
                      >
                        {syncingAccountId === getAccountId(account) ? 'Syncing…' : 'Sync trades'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="rounded border border-zinc-100 bg-zinc-100 p-3 text-sm dark:border-zinc-700 dark:bg-zinc-800">
                <div className="font-medium text-zinc-800 dark:text-zinc-100">No linked accounts yet.</div>
                <div className="text-xs text-zinc-500">Connect a broker to start importing verified account data.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// CHANGE PASSWORD MODAL
interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ open, onClose }) => {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      setError(null);
      setSuccess(false);
    }
  }, [open]);

  const validate = () => {
    if (!currentPw || !newPw || !confirmPw) return "Please fill out all fields.";
    if (newPw.length < 8) return "New password must be at least 8 characters.";
    if (newPw !== confirmPw) return "New password and confirmation do not match.";
    if (newPw === currentPw) return "New password must be different from current password.";
    return null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setError(null);
    // Simulate server save
    setTimeout(() => {
      setSuccess(true);
      setTimeout(() => onClose(), 1000);
    }, 350);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      {/* Dialog */}
      <div role="dialog" aria-modal="true" aria-labelledby="change-password-title" className="relative z-10 w-full max-w-md rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-xl p-6">
        <h3 id="change-password-title" className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Change Password</h3>
        <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">Update your account password. Choose a strong, unique password.</p>
        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <div>
            <label htmlFor="current-pw" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">Current password</label>
            <input id="current-pw" type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} className="w-full px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600" />
          </div>
          <div>
            <label htmlFor="new-pw" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">New password</label>
            <input id="new-pw" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="w-full px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600" />
          </div>
          <div>
            <label htmlFor="confirm-pw" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">Confirm new password</label>
            <input id="confirm-pw" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="w-full px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600" />
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          {success && <p className="text-sm text-green-600 dark:text-green-400">Password changed successfully.</p>}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-200 bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800">Cancel</button>
            <button type="submit" className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface ChangeDisplayNameModalProps {
  open: boolean;
  currentDisplayName: string;
  onClose: () => void;
  onSave: (displayName: string) => Promise<void>;
}

const ChangeDisplayNameModal: React.FC<ChangeDisplayNameModalProps> = ({ open, currentDisplayName, onClose, onSave }) => {
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDisplayName(currentDisplayName);
    setError(null);
    setSaving(false);
  }, [open, currentDisplayName]);

  const validate = () => {
    const nextDisplayName = displayName.trim();
    if (!nextDisplayName) return 'Display name is required.';
    if (nextDisplayName.length > DISPLAY_NAME_MAX_LENGTH) return 'Display name must be 40 characters or fewer.';
    return null;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const nextDisplayName = displayName.trim();
    setSaving(true);
    setError(null);
    try {
      await onSave(nextDisplayName);
      onClose();
    } catch (saveError) {
      setError(String((saveError as Error)?.message || 'Unable to update display name.'));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/50" onClick={saving ? undefined : onClose} aria-hidden />
      <div role="dialog" aria-modal="true" aria-labelledby="change-display-name-title" className="relative z-10 w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-6 shadow-xl dark:border-zinc-700 dark:bg-zinc-900">
        <h3 id="change-display-name-title" className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Change display name</h3>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">Update the name shown across your workspace.</p>
        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <div>
            <label htmlFor="display-name-input" className="mb-1 block text-sm font-medium text-zinc-700 dark:text-zinc-300">Display name</label>
            <input
              id="display-name-input"
              type="text"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              maxLength={DISPLAY_NAME_MAX_LENGTH}
              autoFocus
              className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-zinc-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
            />
          </div>
          {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Lightweight avatar crop modal (no external deps)
const AvatarCropModal: React.FC<{
  open: boolean;
  src: string | null;
  size?: number;
  onCancel: () => void;
  onSave: (dataUrl: string) => void;
}> = ({ open, src, size = 256, onCancel, onSave }) => {
  const viewport = 360; // visual crop viewport (square)
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [natural, setNatural] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [baseSize, setBaseSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ sx: number; sy: number; ox: number; oy: number } | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    if (!open) {
      setZoom(1);
      setOffset({ x: 0, y: 0 });
      setNatural({ w: 0, h: 0 });
      setBaseSize({ w: 0, h: 0 });
    }
  }, [open, src]);

  const onImgLoad = useCallback(() => {
    if (!imgRef.current) return;
    const w = imgRef.current.naturalWidth;
    const h = imgRef.current.naturalHeight;
    setNatural({ w, h });
    const baseScale = Math.max(viewport / Math.max(1, w), viewport / Math.max(1, h));
    setBaseSize({ w: Math.round(w * baseScale), h: Math.round(h * baseScale) });
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  const startPan = useCallback((clientX: number, clientY: number) => {
    dragRef.current = { sx: clientX, sy: clientY, ox: offset.x, oy: offset.y };
  }, [offset.x, offset.y]);

  const onPointerDown = (ev: React.PointerEvent) => {
    (ev.target as Element).setPointerCapture?.(ev.nativeEvent.pointerId);
    startPan(ev.clientX, ev.clientY);
  };

  const onPointerMove = (ev: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = ev.clientX - dragRef.current.sx;
    const dy = ev.clientY - dragRef.current.sy;
    setOffset({ x: dragRef.current.ox + dx, y: dragRef.current.oy + dy });
  };

  const onPointerUp = (ev: React.PointerEvent) => {
    try { (ev.target as Element).releasePointerCapture?.(ev.nativeEvent.pointerId); } catch {}
    dragRef.current = null;
  };

  const handleSave = async () => {
    if (!imgRef.current || natural.w === 0 || natural.h === 0) return;
    const img = imgRef.current;
    const baseW = baseSize.w;
    const baseH = baseSize.h;
    const displayW = baseW * zoom;
    const displayH = baseH * zoom;
    // left/top of image relative to viewport (viewport origin at 0,0)
    const left = (viewport - displayW) / 2 + offset.x;
    const top = (viewport - displayH) / 2 + offset.y;

    const scaleX = natural.w / displayW;
    const scaleY = natural.h / displayH;

    const sx = Math.max(0, (0 - left) * scaleX);
    const sy = Math.max(0, (0 - top) * scaleY);
    const sWidth = Math.min(natural.w - sx, viewport * scaleX);
    const sHeight = Math.min(natural.h - sy, viewport * scaleY);

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, size, size);
    // circular crop
    ctx.save();
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    try {
      ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, size, size);
    } catch {
      // fallback: draw the entire image centered
      ctx.drawImage(img, 0, 0, natural.w, natural.h, 0, 0, size, size);
    }
    ctx.restore();
    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
  };

  // draw preview to match the saved output
  useEffect(() => {
    if (!previewCanvasRef.current) return;
    if (!imgRef.current) return;
    if (natural.w === 0 || baseSize.w === 0) return;
    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const previewSize = 80;
    canvas.width = previewSize;
    canvas.height = previewSize;
    ctx.clearRect(0, 0, previewSize, previewSize);

    const baseW = baseSize.w;
    const baseH = baseSize.h;
    const displayW = baseW * zoom;
    const displayH = baseH * zoom;
    const left = (viewport - displayW) / 2 + offset.x;
    const top = (viewport - displayH) / 2 + offset.y;

    const scaleX = natural.w / displayW;
    const scaleY = natural.h / displayH;

    const sx = Math.max(0, (0 - left) * scaleX);
    const sy = Math.max(0, (0 - top) * scaleY);
    const sWidth = Math.min(natural.w - sx, viewport * scaleX);
    const sHeight = Math.min(natural.h - sy, viewport * scaleY);

    ctx.save();
    ctx.beginPath();
    ctx.arc(previewSize / 2, previewSize / 2, previewSize / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    try {
      ctx.drawImage(imgRef.current, sx, sy, sWidth, sHeight, 0, 0, previewSize, previewSize);
    } catch {
      ctx.drawImage(imgRef.current, 0, 0, natural.w, natural.h, 0, 0, previewSize, previewSize);
    }
    ctx.restore();
  }, [src, natural, baseSize, zoom, offset]);

  if (!open || !src) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/60" onClick={onCancel} aria-hidden />
      <div role="dialog" aria-modal className="relative z-10 w-full max-w-4xl rounded-xl bg-white p-6 shadow-xl dark:bg-zinc-900">
        <div className="flex flex-col gap-4 md:flex-row">
          <div className="relative flex-shrink-0">
            <div className="h-[360px] w-[360px] overflow-hidden rounded-md bg-black/5">
              <div
                className="relative h-[360px] w-[360px] touch-none"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
              >
                <img
                  ref={imgRef}
                  src={src}
                  alt="Crop source"
                  onLoad={onImgLoad}
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: `${baseSize.w}px`,
                    height: `${baseSize.h}px`,
                    transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${zoom})`,
                    transformOrigin: 'center center',
                    userSelect: 'none',
                    touchAction: 'none',
                  }}
                />
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="h-[320px] w-[320px] rounded-full border-2 border-white/90 shadow-lg" />
                </div>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">Drag to reposition, use zoom to scale</div>
          </div>

          <div className="flex flex-1 flex-col items-center justify-between gap-4">
            <div className="w-full">
              <div className="mb-3 text-sm font-medium text-zinc-900 dark:text-zinc-100">Preview</div>
              <div className="flex items-center gap-4">
                <div className="h-20 w-20 overflow-hidden rounded-full border border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800">
                  <canvas ref={previewCanvasRef} width={80} height={80} className="h-full w-full block" style={{ borderRadius: '9999px' }} aria-hidden />
                </div>
                <div className="flex-1">
                  <input aria-label="zoom" type="range" min={0.5} max={3} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="w-full" />
                </div>
              </div>
            </div>

            <div className="mt-4 flex w-full items-center justify-end gap-3">
              <button type="button" onClick={onCancel} className="rounded-md border px-4 py-2 text-sm">Cancel</button>
              <button type="button" onClick={handleSave} className="rounded-md bg-blue-600 px-4 py-2 text-sm text-white">Save</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AccountSection: React.FC<{
  accountIdentity?: AccountIdentity | null;
  billingState?: BillingAccessState | null;
  billingReady?: boolean;
  avatarImage: string | null;
  openAvatarPicker: () => void;
  onOpenDisplayNameModal: () => void;
  onAvatarDrop: (event: React.DragEvent<HTMLButtonElement>) => void;
  onAvatarKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  onRemoveAvatar: () => void;
  avatarError: string | null;
  onOpenPasswordModal: () => void;
  isDark: boolean;
}> = ({ accountIdentity, billingState, billingReady, avatarImage, openAvatarPicker, onOpenDisplayNameModal, onAvatarDrop, onAvatarKeyDown, onRemoveAvatar, avatarError, onOpenPasswordModal, isDark }) => {
  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,2.25fr)_minmax(320px,1fr)]">
      <UserInfoContent
        accountIdentity={accountIdentity}
        avatarImage={avatarImage}
        openAvatarPicker={openAvatarPicker}
        onOpenDisplayNameModal={onOpenDisplayNameModal}
        onAvatarDrop={onAvatarDrop}
        onAvatarKeyDown={onAvatarKeyDown}
        onRemoveAvatar={onRemoveAvatar}
        avatarError={avatarError}
        billingState={billingState}
        billingReady={billingReady}
        isDark={isDark}
      />

      <SecurityCard onOpenPasswordModal={onOpenPasswordModal} isDark={isDark} />
    </div>
  );
};

const PreferencesSection: React.FC<{
  theme?: 'light' | 'dark';
  onThemeChange?: (t: 'light' | 'dark') => void;
}> = ({ theme, onThemeChange }) => {
  const isDark = theme ? theme === 'dark' : (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
  const currentTheme = theme ?? (isDark ? 'dark' : 'light');

  const applyTheme = (nextTheme: 'light' | 'dark') => {
    onThemeChange?.(nextTheme);
    try { localStorage.setItem('theme', nextTheme); } catch {}
    if (nextTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const cardClassName = isDark
    ? 'flex flex-col justify-between rounded-lg border border-slate-800/80 bg-[#0b0f14] p-6 text-white shadow-[0_24px_90px_rgba(0,0,0,0.45)] md:p-8'
    : 'flex flex-col justify-between rounded-lg border border-zinc-200 bg-white p-6 shadow-sm md:p-8';
  const eyebrowClassName = isDark
    ? 'mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500'
    : 'mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500';
  const titleClassName = isDark ? 'mb-1 text-2xl font-bold text-white' : 'mb-1 text-2xl font-bold text-zinc-950';
  const descriptionClassName = isDark ? 'text-sm text-gray-500' : 'text-sm text-zinc-500';
  const footerClassName = isDark
    ? 'mt-8 border-t border-slate-800/60 pt-6'
    : 'mt-8 border-t border-zinc-200/70 pt-6';
  const footerTextClassName = isDark
    ? 'text-xs italic leading-relaxed text-gray-500'
    : 'text-xs italic leading-relaxed text-zinc-500';

  return (
    <div className="grid grid-cols-1 gap-6 animate-in fade-in slide-in-from-bottom-2 duration-300 md:grid-cols-2">
        <section className={cardClassName}>
        <div className="space-y-6">
          <div>
            <div className={eyebrowClassName}>Appearance</div>
            <h3 className={titleClassName}>Theme</h3>
            <p className={descriptionClassName}>Choose the look you want at launch.</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {([
              { key: 'light' as const, label: 'Light Theme' },
              { key: 'dark' as const, label: 'Dark Theme' },
            ]).map((option) => {
              const selected = currentTheme === option.key;
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => applyTheme(option.key)}
                  aria-pressed={selected}
                  className={`flex items-center gap-3 rounded-full border px-6 py-2.5 transition-all ${selected ? isDark ? 'border-blue-500 bg-blue-500/5 text-white' : 'border-blue-500 bg-blue-50 text-zinc-950' : isDark ? 'border-gray-800 bg-transparent text-gray-500 hover:border-gray-700' : 'border-zinc-200 bg-transparent text-zinc-500 hover:border-zinc-300'}`}
                >
                    <div className={`flex h-3.5 w-3.5 items-center justify-center rounded-full border ${selected ? 'border-blue-500' : isDark ? 'border-slate-600' : 'border-zinc-400'}`}>
                    {selected ? <div className="h-1.5 w-1.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]" /> : null}
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-[0.18em]">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className={footerClassName}>
          <p className={footerTextClassName}>Theme changes are applied instantly and synced across sessions.</p>
        </div>
        </section>

        <section className={cardClassName}>
        <div className="space-y-6">
          <div>
            <div className={eyebrowClassName}>Currency</div>
            <h3 className={titleClassName}>Base currency</h3>
            <p className={`${descriptionClassName} leading-relaxed`}>Used for balances, P&amp;L, and reports.</p>
          </div>

          <div className="space-y-4">
            <div className="group relative w-full">
              <div className={`flex cursor-not-allowed items-center justify-between rounded-xl border px-4 py-3 text-sm opacity-80 ${isDark ? 'border-slate-800 bg-[#080b10] text-white' : 'border-zinc-200 bg-zinc-50 text-zinc-950'}`}>
                <span className="font-bold tracking-tight">USD (US Dollar)</span>
                <ChevronDown size={14} className={isDark ? 'text-gray-500' : 'text-zinc-500'} />
              </div>
            </div>

            <div className={`flex items-start gap-2 rounded-lg border p-3 ${isDark ? 'border-slate-800 bg-[#090d12]' : 'border-blue-200 bg-blue-50'}`}>
              <Lock size={12} className={`mt-0.5 shrink-0 ${isDark ? 'text-gray-400' : 'text-blue-600'}`} />
              <p className={`text-[10px] font-medium ${isDark ? 'text-gray-400' : 'text-blue-700/80'}`}>
                Base currency is locked to your current subscription plan.
              </p>
            </div>
          </div>
        </div>

        <div className={footerClassName}>
          <p className={footerTextClassName}>Locked settings can be managed via the Billing portal.</p>
        </div>
      </section>
    </div>
  );
};

const TradingSetupSection: React.FC<{
  isDark: boolean;
  tradingSettingsDraft: TradingSettingsFormState;
  setTradingSettingsDraft: React.Dispatch<React.SetStateAction<TradingSettingsFormState>>;
  onSaveConfiguration: () => void;
  onResetConfiguration: () => void;
}> = ({ isDark, tradingSettingsDraft, setTradingSettingsDraft, onSaveConfiguration, onResetConfiguration }) => {
  const { preference: displayTimezonePreference, timeZone: resolvedDisplayTimezone, savePreference: saveDisplayTimezonePreference } = useDisplayTimezonePreference();
  const [displayTimezoneError, setDisplayTimezoneError] = useState('');
  const isWarriorTradingTenant = currentRuntime.tenant.id === 'warrior-trading';
  const [assetVisibility] = useAssetClassVisibility();
  const sectionClassName = isDark ? 'space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300' : 'space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300';
  const cardClassName = isDark
    ? 'rounded-lg border border-slate-800/80 bg-[#0b0f14] p-6 text-white shadow-[0_24px_90px_rgba(0,0,0,0.45)] md:p-8'
    : 'rounded-lg border border-zinc-200 bg-white p-6 shadow-sm text-zinc-900 md:p-8';
  const subLabelClassName = isDark ? 'text-xs font-semibold text-gray-400' : 'text-xs font-semibold text-zinc-500';
  const bodyTextClassName = isDark ? 'text-sm text-gray-500' : 'text-sm text-zinc-500';
  const mutedTextClassName = isDark ? 'text-xs text-gray-500' : 'text-xs text-zinc-500';
  const riskRowListClassName = isDark ? 'divide-y divide-slate-800/60' : 'divide-y divide-zinc-200/70';
  const riskRowTitleClassName = isDark ? 'text-sm font-bold text-white' : 'text-sm font-bold text-zinc-950';
  const riskRowDescriptionClassName = isDark ? 'text-xs text-gray-500' : 'text-xs text-zinc-500';
  const [saveButtonLabel, setSaveButtonLabel] = React.useState<'Save Configuration' | 'Saved'>('Save Configuration');
  const [selectedDefaultIndicatorId, setSelectedDefaultIndicatorId] = React.useState<ChartIndicatorSettingId>(chartIndicatorOptions[0].id);
  const markConfigurationDirty = () => {
    setSaveButtonLabel('Save Configuration');
  };
  const updateFeeDefault = (field: keyof TradingSettingsFormState['feeDefaults']) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    markConfigurationDirty();
    setTradingSettingsDraft((prev) => ({
      ...prev,
      feeDefaults: {
        ...prev.feeDefaults,
        [field]: nextValue,
      },
    }));
  };

  const formatDisplayValue = (value: string) => {
    // Show explicit two-decimal zero for the exact default zero string only.
    // Avoid formatting empty string so user clears/starts typing without the UI
    // immediately replacing their input.
    if (value === '0') return '0.00';
    return value;
  };

  const normalizeToTwoDecimalsString = (raw: string) => {
    const trimmed = String(raw || '').trim();
    const numeric = Number(trimmed);
    const valid = Number.isFinite(numeric) && numeric >= 0;
    const normalized = valid ? numeric : 0;
    return normalized.toFixed(2);
  };

  const persistTradingSettingsDraft = (nextDraft: TradingSettingsFormState) => {
    saveTradingSettings(tradingSettingsFromFormState(nextDraft));
  };

  const blurFeeDefault = (field: keyof TradingSettingsFormState['feeDefaults']) => () => {
    const nextDraft = {
      ...tradingSettingsDraft,
      feeDefaults: {
        ...tradingSettingsDraft.feeDefaults,
        [field]: normalizeToTwoDecimalsString(tradingSettingsDraft.feeDefaults[field]),
      },
    };
    setTradingSettingsDraft(nextDraft);
    persistTradingSettingsDraft(nextDraft);
  };

  const blurRiskRule = (field: keyof TradingSettingsFormState['riskRules']) => () => {
    const nextDraft = {
      ...tradingSettingsDraft,
      riskRules: {
        ...tradingSettingsDraft.riskRules,
        [field]: normalizeToTwoDecimalsString(tradingSettingsDraft.riskRules[field]),
      },
    };
    setTradingSettingsDraft(nextDraft);
    persistTradingSettingsDraft(nextDraft);
  };

  const updateRiskRule = (field: keyof TradingSettingsFormState['riskRules']) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    markConfigurationDirty();
    setTradingSettingsDraft((prev) => ({
      ...prev,
      riskRules: {
        ...prev.riskRules,
        [field]: nextValue,
      },
    }));
  };

  const updateAnalyticsDefaultAssetClass = (nextValue: AnalyticsAssetClass) => {
    const nextDraft = {
      ...tradingSettingsDraft,
      analyticsDefaultAssetClass: nextValue,
    };
    setTradingSettingsDraft(nextDraft);
    persistTradingSettingsDraft(nextDraft);
  };

  const [chartSettings, setChartSettings, resetChartSettings] = useChartSettings();
  const updateChartSettings = (partial: Parameters<typeof setChartSettings>[0]) => {
    markConfigurationDirty();
    setChartSettings(partial);
  };
  const selectedIndicatorIds = chartSettings.defaultIndicatorIds;
  const availableIndicatorOptions = chartIndicatorOptions.filter((option) => !selectedIndicatorIds.includes(option.id));
  const selectedIndicatorStillAvailable = availableIndicatorOptions.some((option) => option.id === selectedDefaultIndicatorId);
  const indicatorSelectValue = selectedIndicatorStillAvailable ? selectedDefaultIndicatorId : availableIndicatorOptions[0]?.id ?? '';
  const isDefaultChartConfiguration =
    chartSettings.defaultTimeframe === chartSettingsDefaults.defaultTimeframe &&
    chartSettings.defaultIndicatorIds.length === chartSettingsDefaults.defaultIndicatorIds.length &&
    chartSettings.defaultIndicatorIds.every((id, index) => id === chartSettingsDefaults.defaultIndicatorIds[index]) &&
    chartSettings.playbackSpeed === chartSettingsDefaults.playbackSpeed &&
    chartSettings.followMode === chartSettingsDefaults.followMode &&
    chartSettings.lockScrolling === chartSettingsDefaults.lockScrolling &&
    chartSettings.showTradeMarkers === chartSettingsDefaults.showTradeMarkers &&
    chartSettings.showTradeDrawings === chartSettingsDefaults.showTradeDrawings &&
    chartSettings.showExecutions === chartSettingsDefaults.showExecutions &&
    chartSettings.showPnlOverlay === chartSettingsDefaults.showPnlOverlay &&
    chartSettings.showLegend === chartSettingsDefaults.showLegend &&
    chartSettings.hideVolumeFromCharts === chartSettingsDefaults.hideVolumeFromCharts &&
    chartSettings.timezoneDisplay === chartSettingsDefaults.timezoneDisplay;
  const runtimeTradingSettingsDefaults = getRuntimeTradingSettingsDefaults();
  const isDefaultTradingConfiguration =
    tradingSettingsDraft.feeDefaults.stocks === String(runtimeTradingSettingsDefaults.feeDefaults.stocks) &&
    tradingSettingsDraft.feeDefaults.futures === String(runtimeTradingSettingsDefaults.feeDefaults.futures) &&
    tradingSettingsDraft.feeDefaults.forex === String(runtimeTradingSettingsDefaults.feeDefaults.forex) &&
    tradingSettingsDraft.feeDefaults.crypto === String(runtimeTradingSettingsDefaults.feeDefaults.crypto) &&
    tradingSettingsDraft.riskRules.defaultRiskPerTrade === String(runtimeTradingSettingsDefaults.riskRules.defaultRiskPerTrade) &&
    tradingSettingsDraft.riskRules.dailyLossLimit === String(runtimeTradingSettingsDefaults.riskRules.dailyLossLimit) &&
    tradingSettingsDraft.analyticsDefaultAssetClass === runtimeTradingSettingsDefaults.analyticsDefaultAssetClass;
  const handleSaveClick = () => {
    onSaveConfiguration();
    setSaveButtonLabel('Saved');
  };
  const handleResetClick = () => {
    resetChartSettings();
    onResetConfiguration();
    setSaveButtonLabel('Saved');
  };

  return (
    <div className={sectionClassName}>
      <section
        className={cardClassName}
        onChangeCapture={(event) => {
          const target = event.target as HTMLElement | null;
          if (target?.closest('[data-autosave="true"]')) return;
          markConfigurationDirty();
        }}
      >
          <div className="border-b border-slate-800/50 pb-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-4">
              {/* decorative dot removed per design request */}
              <div>
                <h3 className={isDark ? 'text-2xl font-bold text-white' : 'text-2xl font-bold text-zinc-950'}>Chart Configuration</h3>
                <p className={bodyTextClassName}>Configure the default experience for your workspace.</p>
              </div>
            </div>

            <div className="flex items-center md:items-start md:ml-4">
              {/* Status pill: uses existing booleans to preserve logic but renders a compact badge */}
                {isDefaultChartConfiguration && isDefaultTradingConfiguration ? (
                <span className="inline-flex items-center rounded-md px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800/40">
                  DEFAULT CONFIGURATION
                </span>
              ) : (
                <span className="inline-flex items-center rounded-md px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] bg-amber-50 text-amber-800 border border-amber-200 text-xs dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800/40">
                  MODIFIED
                </span>
              )}
            </div>

          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 pt-6 md:grid-cols-2 lg:grid-cols-4">
          <section className="space-y-4">
            <div className="flex items-center gap-3 border-b border-zinc-200 pb-3 dark:border-slate-800/50">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/10 text-blue-400">
                <BarChart3 size={16} />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-400">01. Chart Defaults</div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className={subLabelClassName}>Chart type</label>
                <div className="relative">
                  <select value={chartSettings.chartType} onChange={(e) => updateChartSettings({ chartType: e.target.value as 'candles' | 'heikin' })} className="w-full appearance-none rounded-xl border border-zinc-300 bg-white px-4 select-chevron-input py-3 text-sm text-zinc-900 outline-none focus:border-blue-500/50 dark:border-slate-800 dark:bg-[#080b10] dark:text-white">
                    <option value="candles">Candles</option>
                    <option value="heikin">Heikin Ashi</option>
                  </select>
                  <ChevronDown size={14} className="pointer-events-none absolute select-chevron-icon top-1/2 -translate-y-1/2 text-zinc-500 dark:text-gray-500" />
                </div>
              </div>

              <div className="space-y-2">
                <label className={subLabelClassName}>Time frame</label>
                <div className="relative">
                  <select value={chartSettings.defaultTimeframe} onChange={(e) => updateChartSettings({ defaultTimeframe: e.target.value })} className="w-full appearance-none rounded-xl border border-zinc-300 bg-white px-4 select-chevron-input py-3 text-sm text-zinc-900 outline-none focus:border-blue-500/50 dark:border-slate-800 dark:bg-[#080b10] dark:text-white">
                    <option value="1m">1-minute</option>
                    <option value="5m">5-minute</option>
                    <option value="15m">15-minute</option>
                    <option value="1h">1-hour</option>
                    <option value="1d">Daily</option>
                  </select>
                  <ChevronDown size={14} className="pointer-events-none absolute select-chevron-icon top-1/2 -translate-y-1/2 text-zinc-500 dark:text-gray-500" />
                </div>
              </div>

              <div className="space-y-2">
                <label className={subLabelClassName}>Time display</label>
                <div className="relative">
                  <select
                    value={displayTimezonePreference.mode}
                    onChange={(event) => {
                      setDisplayTimezoneError('');
                      const next = event.target.value === 'manual'
                        ? { mode: 'manual' as const, timeZone: resolvedDisplayTimezone }
                        : { mode: 'automatic' as const };
                      void saveDisplayTimezonePreference(next).catch((error) => setDisplayTimezoneError(error instanceof Error ? error.message : 'Could not save timezone preference.'));
                    }}
                    className="w-full appearance-none rounded-xl border border-zinc-300 bg-white px-4 select-chevron-input py-3 text-sm text-zinc-900 outline-none focus:border-blue-500/50 dark:border-slate-800 dark:bg-[#080b10] dark:text-white"
                  >
                    <option value="automatic">Automatic (Computer Timezone)</option>
                    <option value="manual">Manual timezone</option>
                  </select>
                  <ChevronDown size={14} className="pointer-events-none absolute select-chevron-icon top-1/2 -translate-y-1/2 text-zinc-500 dark:text-gray-500" />
                </div>
                {displayTimezonePreference.mode === 'manual' && (
                  <div className="relative mt-2">
                    <select
                      aria-label="Manual display timezone"
                      value={displayTimezonePreference.timeZone}
                      onChange={(event) => {
                        setDisplayTimezoneError('');
                        void saveDisplayTimezonePreference({ mode: 'manual', timeZone: event.target.value })
                          .catch((error) => setDisplayTimezoneError(error instanceof Error ? error.message : 'Could not save timezone preference.'));
                      }}
                      className="w-full appearance-none rounded-xl border border-zinc-300 bg-white px-4 select-chevron-input py-3 text-sm text-zinc-900 outline-none focus:border-blue-500/50 dark:border-slate-800 dark:bg-[#080b10] dark:text-white"
                    >
                      {extendedTimezones.map((timezone) => <option key={timezone.value} value={timezone.value}>{timezone.label}</option>)}
                    </select>
                    <ChevronDown size={14} className="pointer-events-none absolute select-chevron-icon top-1/2 -translate-y-1/2 text-zinc-500 dark:text-gray-500" />
                  </div>
                )}
                <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">Trade dates remain tied to their existing trading-session rules. Current display timezone: {resolvedDisplayTimezone}.</p>
                {displayTimezoneError && <p role="alert" className="mt-1 text-xs text-red-600 dark:text-red-400">{displayTimezoneError}</p>}
              </div>

            </div>
          </section>

          <section className="space-y-4 md:border-l md:border-zinc-200 md:pl-6 dark:md:border-slate-800/40">
            <div className="flex items-center gap-3 border-b border-zinc-200 pb-3 dark:border-slate-800/50">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/10 text-blue-400">
                <Activity size={16} />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-400">02. Default Indicators</div>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="defaultIndicatorSelect" className={subLabelClassName}>Default Indicators</label>
              <div className="flex min-h-[152px] flex-col">
                <div className="space-y-3">
                  <div className="relative">
                    <select
                      id="defaultIndicatorSelect"
                      value={indicatorSelectValue}
                      onChange={(event) => {
                        if (!event.target.value) return;
                        setSelectedDefaultIndicatorId(event.target.value as ChartIndicatorSettingId);
                      }}
                      className="w-full appearance-none rounded-xl border border-zinc-300 bg-white px-4 select-chevron-input py-3 text-sm text-zinc-900 outline-none focus:border-blue-500/50 dark:border-slate-800 dark:bg-[#080b10] dark:text-white"
                    >
                      {availableIndicatorOptions.length === 0 ? (
                        <option value="">All indicators added</option>
                      ) : (
                        availableIndicatorOptions.map((option) => (
                          <option key={option.id} value={option.id}>{option.label}</option>
                        ))
                      )}
                    </select>
                    <ChevronDown size={14} className="pointer-events-none absolute select-chevron-icon top-1/2 -translate-y-1/2 text-zinc-500 dark:text-gray-500" />
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {selectedIndicatorIds.length === 0 ? (
                      <span className={mutedTextClassName}>No default indicators selected.</span>
                    ) : (
                      selectedIndicatorIds.map((indicatorId) => (
                        <button
                          key={indicatorId}
                          type="button"
                          title="click to remove"
                          aria-label={`Remove ${getChartIndicatorLabel(indicatorId)} default indicator`}
                          onClick={() => updateChartSettings({ defaultIndicatorIds: selectedIndicatorIds.filter((value) => value !== indicatorId) })}
                          className="rounded-full border border-zinc-300 bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-700 transition-all hover:border-zinc-400 hover:text-zinc-950 dark:border-slate-700 dark:bg-[#10141b] dark:text-slate-200 dark:hover:border-slate-500 dark:hover:text-white"
                        >
                          {getChartIndicatorLabel(indicatorId)}
                        </button>
                      ))
                    )}
                  </div>
                </div>

                <div className="mt-5 flex justify-end pt-1 sm:mt-6">
                  <button
                    type="button"
                    disabled={!indicatorSelectValue}
                    onClick={() => {
                      if (!indicatorSelectValue) return;
                      updateChartSettings({ defaultIndicatorIds: [...selectedIndicatorIds, indicatorSelectValue as ChartIndicatorSettingId] });
                      const nextAvailable = availableIndicatorOptions.filter((option) => option.id !== indicatorSelectValue)[0];
                      if (nextAvailable) {
                        setSelectedDefaultIndicatorId(nextAvailable.id);
                      }
                    }}
                    className="inline-flex max-w-full items-center justify-center gap-1.5 self-end rounded-lg border border-zinc-300 bg-white px-3.5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-700 transition-all hover:bg-zinc-100 hover:text-zinc-950 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700/80 dark:bg-[#0d1218] dark:text-slate-200 dark:hover:bg-[#161b24] dark:hover:text-white"
                  >
                    <Plus size={14} /> Add indicator
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-4 md:border-l md:border-zinc-200 md:pl-6 dark:md:border-zinc-800/50">
            <div className="flex items-center gap-3 border-b border-zinc-200 pb-3 dark:border-slate-800/50">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/10 text-blue-400">
                <BadgeCheck size={16} />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-400">03. Interface</div>
              </div>
            </div>

            <div className="space-y-4 pt-1">
              <div className="flex items-center gap-3 text-sm leading-tight">
                <input id="showTradeMarkers" type="checkbox" checked={chartSettings.showTradeMarkers} onChange={() => updateChartSettings({ showTradeMarkers: !chartSettings.showTradeMarkers })} className="h-4 w-4 shrink-0 rounded border-zinc-300 bg-white text-blue-600 dark:border-slate-800 dark:bg-[#080b10] dark:text-blue-500" />
                <div className="flex min-w-0 items-center gap-1.5">
                  <label htmlFor="showTradeMarkers" className="cursor-pointer select-none">Show trade markers</label>
                  <ChartSettingTooltip
                    isDark={isDark}
                    copy={{
                      title: 'Trade markers',
                      description: 'Shows trade-level entry and exit markers on the chart.',
                      visual: 'Entry ▲ → Exit ▼',
                      result: 'easier trade review',
                    }}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm leading-tight">
                <input id="showTradeDrawings" type="checkbox" checked={chartSettings.showTradeDrawings} onChange={() => updateChartSettings({ showTradeDrawings: !chartSettings.showTradeDrawings })} className="h-4 w-4 shrink-0 rounded border-zinc-300 bg-white text-blue-600 dark:border-slate-800 dark:bg-[#080b10] dark:text-blue-500" />
                <label htmlFor="showTradeDrawings" className="cursor-pointer select-none">Trade drawings</label>
              </div>
              <div className="flex items-center gap-3 text-sm leading-tight">
                <input id="showExecutions" type="checkbox" checked={chartSettings.showExecutions} onChange={() => updateChartSettings({ showExecutions: !chartSettings.showExecutions })} className="h-4 w-4 shrink-0 rounded border-zinc-300 bg-white text-blue-600 dark:border-slate-800 dark:bg-[#080b10] dark:text-blue-500" />
                <div className="flex min-w-0 items-center gap-1.5">
                  <label htmlFor="showExecutions" className="cursor-pointer select-none">Show executions</label>
                  <ChartSettingTooltip
                    isDark={isDark}
                    copy={{
                      title: 'Executions',
                      description: 'Shows individual broker fills inside a trade.',
                      visual: 'Buy • Buy • Sell',
                      result: 'shows how the position was built',
                    }}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm leading-tight">
                <input id="showPnlOverlay" type="checkbox" checked={chartSettings.showPnlOverlay} onChange={() => updateChartSettings({ showPnlOverlay: !chartSettings.showPnlOverlay })} className="h-4 w-4 shrink-0 rounded border-zinc-300 bg-white text-blue-600 dark:border-slate-800 dark:bg-[#080b10] dark:text-blue-500" />
                <div className="flex min-w-0 items-center gap-1.5">
                  <label htmlFor="showPnlOverlay" className="cursor-pointer select-none">Show P&amp;L overlay</label>
                  <ChartSettingTooltip
                    isDark={isDark}
                    copy={{
                      title: 'P&L overlay',
                      description: 'Shows profit/loss context directly on the chart.',
                      visual: '+$250 / +2.5R',
                      result: 'faster performance review',
                    }}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm leading-tight">
                <input id="showLegend" type="checkbox" checked={chartSettings.showLegend} onChange={() => updateChartSettings({ showLegend: !chartSettings.showLegend })} className="h-4 w-4 shrink-0 rounded border-zinc-300 bg-white text-blue-600 dark:border-slate-800 dark:bg-[#080b10] dark:text-blue-500" />
                <label htmlFor="showLegend" className="cursor-pointer select-none">Show legend</label>
              </div>
            </div>
          </section>

          <section className="space-y-4 md:border-l md:border-zinc-200 md:pl-6 lg:pl-0 xl:border-l xl:border-zinc-200 xl:pl-6 dark:md:border-zinc-800/50 dark:xl:border-zinc-800/50">
            <div className="flex items-center gap-3 border-b border-zinc-200 pb-3 dark:border-slate-800/50">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/10 text-blue-400">
                <ShieldCheck size={16} />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-400">04. Control</div>
              </div>
            </div>

            <div className="space-y-5 pt-1">
              <div className="space-y-2">
                <label className={subLabelClassName}>Playback speed</label>
                <div className="relative">
                  <select value={String(chartSettings.playbackSpeed)} onChange={(e) => updateChartSettings({ playbackSpeed: Number(e.target.value) })} className="w-full appearance-none rounded-xl border border-zinc-300 bg-white px-4 select-chevron-input py-3 text-sm text-zinc-900 outline-none focus:border-blue-500/50 dark:border-slate-800 dark:bg-[#080b10] dark:text-white">
                    <option value="0.5">0.5x</option>
                    <option value="1">1x</option>
                    <option value="2">2x</option>
                    <option value="4">4x</option>
                  </select>
                  <ChevronDown size={14} className="pointer-events-none absolute select-chevron-icon top-1/2 -translate-y-1/2 text-zinc-500 dark:text-gray-500" />
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3 text-sm leading-tight">
                  <input id="followMode" type="checkbox" checked={chartSettings.followMode} onChange={() => updateChartSettings({ followMode: !chartSettings.followMode })} className="h-4 w-4 shrink-0 rounded border-zinc-300 bg-white text-blue-600 dark:border-slate-800 dark:bg-[#080b10] dark:text-blue-500" />
                  <div className="flex min-w-0 items-center gap-1.5">
                    <label htmlFor="followMode" className="cursor-pointer select-none">Center the chart on playback</label>
                    <ChartSettingTooltip
                      isDark={isDark}
                      copy={{
                        title: 'Center on playback',
                        description: 'Keeps the active replay candle centered while reviewing.',
                        visual: '← candles | active candle | candles →',
                        result: 'smoother replay',
                      }}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3 text-sm leading-tight">
                  <input id="lockScrolling" type="checkbox" checked={chartSettings.lockScrolling} onChange={() => updateChartSettings({ lockScrolling: !chartSettings.lockScrolling })} className="h-4 w-4 shrink-0 rounded border-zinc-300 bg-white text-blue-600 dark:border-slate-800 dark:bg-[#080b10] dark:text-blue-500" />
                  <div className="flex min-w-0 items-center gap-1.5">
                    <label htmlFor="lockScrolling" className="cursor-pointer select-none">Lock scrolling</label>
                    <ChartSettingTooltip
                      isDark={isDark}
                      copy={{
                        title: 'Lock scrolling',
                        description: 'Prevents accidental chart movement during review.',
                        visual: 'Chart locked 🔒',
                        result: 'stable review view',
                      }}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3 text-sm leading-tight">
                  <input id="showVolume" type="checkbox" checked={!chartSettings.hideVolumeFromCharts} onChange={() => updateChartSettings({ hideVolumeFromCharts: !chartSettings.hideVolumeFromCharts })} className="h-4 w-4 shrink-0 rounded border-zinc-300 bg-white text-blue-600 dark:border-slate-800 dark:bg-[#080b10] dark:text-blue-500" />
                  <div className="flex min-w-0 items-center gap-1.5">
                    <label htmlFor="showVolume" className="cursor-pointer select-none">Show volume on charts</label>
                    <ChartSettingTooltip
                      isDark={isDark}
                      copy={{
                        title: 'Show volume',
                        description: 'Displays the volume panel on the chart.',
                        visual: 'Volume panel visible',
                        result: 'clearer chart context',
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {!isWarriorTradingTenant ? (
          <section className={`${cardClassName} mt-6`}>
            <div className="flex items-center gap-3 border-b border-zinc-200 pb-3 dark:border-slate-800/50">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/10 text-blue-400">
                <ChartSpline size={16} />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-400">05. Analytics Defaults</div>
              </div>
            </div>

            <div className="mt-6 max-w-2xl space-y-3">
              <div className={subLabelClassName}>Default asset class for analytics</div>
              <div className="flex flex-wrap items-center gap-3" role="group" aria-label="Default asset class for analytics">
                {ANALYTICS_ASSET_CLASS_OPTIONS.map((option) => {
                  const selected = tradingSettingsDraft.analyticsDefaultAssetClass === option;
                  const label = option === 'ALL' ? 'All' : option.charAt(0) + option.slice(1).toLowerCase();

                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => updateAnalyticsDefaultAssetClass(option)}
                      aria-pressed={selected}
                      className={`flex items-center gap-3 rounded-full border px-6 py-2.5 transition-all ${selected ? isDark ? 'border-blue-500 bg-blue-500/5 text-white' : 'border-blue-500 bg-blue-50 text-zinc-950' : isDark ? 'border-gray-800 bg-transparent text-gray-500 hover:border-gray-700' : 'border-zinc-200 bg-transparent text-zinc-500 hover:border-zinc-300'} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40`}
                    >
                      <div className={`flex h-3.5 w-3.5 items-center justify-center rounded-full border ${selected ? 'border-blue-500' : isDark ? 'border-slate-600' : 'border-zinc-400'}`}>
                        {selected ? <div className="h-1.5 w-1.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]" /> : null}
                      </div>
                      <span className="text-[11px] font-bold uppercase tracking-[0.18em]">{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
        ) : null}

        <div className="mt-0 mb-0 grid grid-cols-1 gap-3 pt-5 pb-0 text-xs text-gray-500 dark:border-slate-800/60 md:grid-cols-[1fr_auto_1fr] md:items-center">
          <div className="font-semibold uppercase tracking-[0.18em] text-gray-400 md:justify-self-start"></div>
          <div className="flex flex-col-reverse gap-2 justify-self-center self-center sm:flex-row sm:flex-wrap sm:items-center sm:justify-center">
            <button
              type="button"
              onClick={handleResetClick}
              className="inline-flex items-center justify-center rounded-xl border border-zinc-300 bg-white px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-700 transition-all hover:bg-zinc-100 hover:text-zinc-950 dark:border-slate-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-slate-800/50 dark:hover:text-white"
            >
              Reset to Defaults
            </button>
            <button
              type="button"
              onClick={handleSaveClick}
              className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white shadow-lg shadow-blue-900/20 transition-all hover:bg-blue-500"
            >
              <span className="inline-flex items-center gap-2 leading-none">
                <Zap size={14} /> {saveButtonLabel}
              </span>
            </button>
          </div>
          <div className="hidden md:block" />
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <section className={cardClassName}>
        <div className="mb-6">
          <h3 className={`mb-1 text-2xl font-bold ${isDark ? 'text-white' : 'text-zinc-950'}`}>Fee defaults</h3>
          <p className={bodyTextClassName}>Used when broker data does not include fees.</p>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <span className={mutedTextClassName + ' min-w-[140px]'}>Stocks</span>
            <div className="relative flex-1">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    aria-label="Stocks fee default"
                    data-autosave="true"
                    value={formatDisplayValue(tradingSettingsDraft.feeDefaults.stocks)}
                    onChange={updateFeeDefault('stocks')}
                    onFocus={(e) => e.currentTarget.select()}
                    onBlur={blurFeeDefault('stocks')}
                    placeholder="0.00"
                    className="w-full rounded-xl border border-zinc-300 bg-white py-3 pl-8 pr-4 text-sm text-zinc-900 outline-none focus:border-blue-500/50 dark:border-slate-800 dark:bg-[#05070b] dark:text-white"
                  />
            </div>
            <span className={mutedTextClassName + ' min-w-[80px]'}>per share</span>
          </div>

          {assetVisibility.FUTURES ? (
            <div className="flex items-center gap-4">
              <span className={mutedTextClassName + ' min-w-[140px]'}>Futures</span>
              <div className="relative flex-1">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      aria-label="Futures fee default"
                      data-autosave="true"
                      value={formatDisplayValue(tradingSettingsDraft.feeDefaults.futures)}
                      onChange={updateFeeDefault('futures')}
                      onFocus={(e) => e.currentTarget.select()}
                      onBlur={blurFeeDefault('futures')}
                      placeholder="0.00"
                      className="w-full rounded-xl border border-zinc-300 bg-white py-3 pl-8 pr-4 text-sm text-zinc-900 outline-none focus:border-blue-500/50 dark:border-slate-800 dark:bg-[#05070b] dark:text-white"
                    />
              </div>
              <span className={mutedTextClassName + ' min-w-[80px]'}>per contract</span>
            </div>
          ) : null}

          {assetVisibility.FOREX ? (
            <div className="flex items-center gap-4">
              <span className={mutedTextClassName + ' min-w-[140px]'}>Forex</span>
              <div className="relative flex-1">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      aria-label="Forex fee default"
                      data-autosave="true"
                      value={formatDisplayValue(tradingSettingsDraft.feeDefaults.forex)}
                      onChange={updateFeeDefault('forex')}
                      onFocus={(e) => e.currentTarget.select()}
                      onBlur={blurFeeDefault('forex')}
                      placeholder="0.00"
                      className="w-full rounded-xl border border-zinc-300 bg-white py-3 pl-8 pr-4 text-sm text-zinc-900 outline-none focus:border-blue-500/50 dark:border-slate-800 dark:bg-[#05070b] dark:text-white"
                    />
              </div>
              <span className={mutedTextClassName + ' min-w-[80px]'}>per trade</span>
            </div>
          ) : null}

          {assetVisibility.CRYPTO ? (
            <div className="flex items-center gap-4">
              <span className={mutedTextClassName + ' min-w-[140px]'}>Crypto</span>
              <div className="relative flex-1">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      aria-label="Crypto fee default"
                      data-autosave="true"
                      value={formatDisplayValue(tradingSettingsDraft.feeDefaults.crypto)}
                      onChange={updateFeeDefault('crypto')}
                      onFocus={(e) => e.currentTarget.select()}
                      onBlur={blurFeeDefault('crypto')}
                      placeholder="0.00"
                      className="w-full rounded-xl border border-zinc-300 bg-white py-3 pl-8 pr-4 text-sm text-zinc-900 outline-none focus:border-blue-500/50 dark:border-slate-800 dark:bg-[#05070b] dark:text-white"
                    />
              </div>
              <span className={mutedTextClassName + ' min-w-[80px]'}>per trade</span>
            </div>
          ) : null}
        </div>
      </section>

      <section className={cardClassName}>
        <div className="mb-8">
          <h3 className={`mb-1 text-2xl font-bold ${isDark ? 'text-white' : 'text-zinc-950'}`}>Risk rules</h3>
          <p className={bodyTextClassName}>Set default risk rules for trade review and future analytics.</p>
        </div>

        <div className={riskRowListClassName}>
          <div className="flex flex-col gap-6 py-6 md:flex-row md:items-center md:justify-between md:gap-6 first:pt-0 last:pb-0">
            <div className="space-y-2">
              <div className={riskRowTitleClassName}>Default initial risk per trade</div>
              <p className={riskRowDescriptionClassName}>Used for R-multiple calculations when initial risk is missing.</p>
            </div>

            <div className="flex w-full items-center gap-3 md:w-[300px] md:flex-none md:justify-end">
              <div className="relative flex-1">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label="Default initial risk per trade"
                  data-autosave="true"
                  value={formatDisplayValue(tradingSettingsDraft.riskRules.defaultRiskPerTrade)}
                  onChange={updateRiskRule('defaultRiskPerTrade')}
                  onFocus={(e) => e.currentTarget.select()}
                  onBlur={blurRiskRule('defaultRiskPerTrade')}
                  className="w-full rounded-xl border border-zinc-300 bg-white py-3 pl-8 pr-4 text-sm text-zinc-900 outline-none focus:border-blue-500/50 dark:border-slate-800 dark:bg-[#05070b] dark:text-white"
                />
              </div>
              <span className={mutedTextClassName + ' shrink-0'}>per trade</span>
            </div>
          </div>

          <div className="flex flex-col gap-6 py-6 md:flex-row md:items-center md:justify-between md:gap-6 last:pb-0">
            <div className="space-y-2">
              <div className={riskRowTitleClassName}>Daily loss limit</div>
              <p className={riskRowDescriptionClassName}>Used to flag days where your max daily loss rule is exceeded.</p>
            </div>

            <div className="flex w-full items-center gap-3 md:w-[300px] md:flex-none md:justify-end">
              <div className="relative flex-1">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label="Daily loss limit"
                  data-autosave="true"
                  value={formatDisplayValue(tradingSettingsDraft.riskRules.dailyLossLimit)}
                  onChange={updateRiskRule('dailyLossLimit')}
                  onFocus={(e) => e.currentTarget.select()}
                  onBlur={blurRiskRule('dailyLossLimit')}
                  className="w-full rounded-xl border border-zinc-300 bg-white py-3 pl-8 pr-4 text-sm text-zinc-900 outline-none focus:border-blue-500/50 dark:border-slate-800 dark:bg-[#05070b] dark:text-white"
                />
              </div>
              <span className={mutedTextClassName + ' shrink-0'}>per day</span>
            </div>
          </div>
        </div>
        </section>
      </div>

      <div data-tour-target="settings-charting-setup">
        <SnapTradeCard isDark={isDark} />
      </div>
    </div>
  );
};

// MAIN SettingsPage Component
interface SettingsPageProps {
  accountIdentity?: AccountIdentity | null;
  billingState?: BillingAccessState | null;
  billingReady?: boolean;
  onAvatarChange?: (url: string | null) => void;
  onDisplayNameChange?: (displayName: string) => void;
  theme?: Theme;
  onThemeChange?: (t: Theme) => void;
  adminToolsAccess?: AdminBillingAccessState | null;
  adminToolsAccessReady?: boolean;
  adminToolsAccessError?: string | null;
  onRetryAdminToolsAccess?: () => void;
  initialTabLabel?: string;
  onTabChange?: () => void;
  whiteLabel?: WhiteLabelAppBranding | null;
}
const SettingsPage: React.FC<SettingsPageProps> = ({
  accountIdentity,
  billingState = null,
  billingReady = false,
  onAvatarChange,
  onDisplayNameChange,
  theme,
  onThemeChange,
  adminToolsAccess = null,
  adminToolsAccessReady = false,
  adminToolsAccessError = null,
  onRetryAdminToolsAccess,
  initialTabLabel,
  onTabChange,
  whiteLabel = null,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [avatarImage, setAvatarImage] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [showPwModal, setShowPwModal] = useState(false);
  const [showDisplayNameModal, setShowDisplayNameModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Prefer theme prop (App state) to avoid one-frame inversion; fallback to document class
  const isDark = theme ? theme === 'dark' : (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
  const accountDisplayName = String(accountIdentity?.displayName || '').trim();
  const accountEmail = String(accountIdentity?.email || '').trim();
  const accountInitials = getInitials(accountDisplayName || accountEmail.split('@')[0] || '');
  const showAdminTab = Boolean(
    adminToolsAccessReady
      && (adminToolsAccess?.canAccessAdminTab || adminToolsAccess?.isAdmin),
  );
  const [tradingSettingsDraft, setTradingSettingsDraft] = useState<TradingSettingsFormState>(() => tradingSettingsToFormState(readTradingSettings()));

  useEffect(() => {
    if (currentRuntime.tenant.id !== 'warrior-trading') return;

    const enforcedSettings = {
      ...readTradingSettings(),
      analyticsDefaultAssetClass: 'STOCKS' as const,
    };
    const saved = saveTradingSettings(enforcedSettings);
    setTradingSettingsDraft(tradingSettingsToFormState(saved.settings));
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("avatarDataUrl");
      if (saved) setAvatarImage(saved);
    } catch {}
  }, []);

  const tabs = showAdminTab ? SETTINGS_TABS : SETTINGS_TABS.filter((tab) => tab.label !== 'ADMIN');
  const activeTabIndex = Math.min(activeTab, Math.max(tabs.length - 1, 0));
  const activeTabLabel = tabs[activeTabIndex]?.label ?? SETTINGS_TABS[0].label;

  useEffect(() => {
    if (initialTabLabel) {
      const idx = tabs.findIndex((t) => t.label === initialTabLabel);
      if (idx !== -1) {
        setActiveTab(idx);
      }
    }
  }, [initialTabLabel, tabs]);

  const openAvatarPicker = () => { setAvatarError(null); fileInputRef.current?.click(); };
  const loadImageFile = (file: File) => {
    if (!file) return;
    setAvatarError(null);
    if (!(file.type && file.type.startsWith("image/")) && !isValidImageName(file.name)) {
      setAvatarError("Unsupported file type.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setAvatarError("File too large. Max 8 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = (e.target?.result as string) || null;
      if (dataUrl) {
        // open crop modal instead of saving directly
        setCropSrc(dataUrl);
        setShowCropModal(true);
      }
    };
    reader.readAsDataURL(file);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => { const file = e.target.files?.[0] || null; if (file) loadImageFile(file); e.target.value = ''; };
  const onAvatarDrop = (e: React.DragEvent<HTMLButtonElement>) => { e.preventDefault(); const file = e.dataTransfer.files?.[0]; if (file) loadImageFile(file); };
  const onAvatarKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openAvatarPicker(); } };
  const onRemoveAvatar = () => { setAvatarImage(null); setAvatarError(null); try { localStorage.removeItem("avatarDataUrl"); } catch {}; onAvatarChange?.(null); if (fileInputRef.current) fileInputRef.current.value = ""; };

  const handleCropCancel = useCallback(() => {
    setShowCropModal(false);
    setCropSrc(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleCropSave = useCallback((dataUrl: string) => {
    setShowCropModal(false);
    setCropSrc(null);
    setAvatarImage(dataUrl);
    try { localStorage.setItem('avatarDataUrl', dataUrl); } catch {}
    onAvatarChange?.(dataUrl);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [onAvatarChange]);
  const handleSaveTradingSettings = () => {
    const nextSettings = tradingSettingsFromFormState(tradingSettingsDraft);
    const saved = saveTradingSettings(nextSettings);
    setTradingSettingsDraft(tradingSettingsToFormState(saved.settings));
  };

  const handleResetTradingSettings = () => {
    const reset = resetTradingSettings();
    setTradingSettingsDraft(tradingSettingsToFormState(reset.settings));
  };
  const handleResetChartConfiguration = () => {
    handleResetTradingSettings();
  };
  const handleDisplayNameSave = async (nextDisplayName: string) => {
    await updateCurrentUserDisplayName(nextDisplayName);
    onDisplayNameChange?.(nextDisplayName);
  };

  const renderContent = () => {
    switch (activeTabLabel) {
      case 'USER INFO':
        return (
          <AccountSection
            accountIdentity={accountIdentity}
            billingState={billingState}
            billingReady={billingReady}
            avatarImage={avatarImage}
            openAvatarPicker={openAvatarPicker}
            onOpenDisplayNameModal={() => setShowDisplayNameModal(true)}
            onAvatarDrop={onAvatarDrop}
            onAvatarKeyDown={onAvatarKeyDown}
            onRemoveAvatar={onRemoveAvatar}
            avatarError={avatarError}
            onOpenPasswordModal={() => setShowPwModal(true)}
            isDark={isDark}
          />
        );
      case 'PREFERENCES':
        return <PreferencesSection theme={theme} onThemeChange={onThemeChange} />;
      case 'CHARTING SETUP':
        return (
          <TradingSetupSection
            isDark={isDark}
            tradingSettingsDraft={tradingSettingsDraft}
            setTradingSettingsDraft={setTradingSettingsDraft}
            onSaveConfiguration={handleSaveTradingSettings}
            onResetConfiguration={handleResetChartConfiguration}
          />
        );
      case 'BILLING':
        return (
          <BillingPanel
            embedded
            identity={accountIdentity?.uid ? { uid: accountIdentity.uid, email: accountIdentity.email ?? null } : null}
            initialState={billingState}
            partnerMonthlyPrice={whiteLabel?.partnerMonthlyPrice}
            isAdminAccount={Boolean(adminToolsAccess?.isAdmin || adminToolsAccess?.canAccessAdminTab)}
          />
        );
      case 'ADMIN':
        return (
          <div className="pt-1 sm:pt-2">
            <AdminToolsPage
              theme={isDark ? 'dark' : 'light'}
              accountIdentity={accountIdentity?.uid ? { uid: accountIdentity.uid, email: accountIdentity.email ?? null } : null}
              accessState={adminToolsAccess}
              accessReady={adminToolsAccessReady}
              accessError={adminToolsAccessError}
              onRetryAccess={onRetryAdminToolsAccess}
            />
          </div>
        );
      default:
        return <PlaceholderContent tabName={activeTabLabel} avatarImage={avatarImage} initials={accountInitials} />;
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100 dark:bg-zinc-950 font-sans">
      <div className="mx-auto w-full px-4 pt-10 pb-4 sm:px-6 lg:px-8" style={{ maxWidth: "1480px" }}>
        <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
        <div className="mb-8 flex flex-wrap items-center overflow-x-auto border-b border-blue-500/20 no-scrollbar scroll-smooth divide-x divide-zinc-200 dark:divide-zinc-700">
            {tabs.map((tab, index) => (
              <SettingsTab key={tab.label} tab={tab} isActive={activeTabIndex === index} onClick={() => { setActiveTab(index); onTabChange?.(); }} />
            ))}
        </div>
        <div className="pt-1 sm:pt-1">
    <div className="relative bg-transparent px-0">
      {renderContent()}
      <ChangePasswordModal open={showPwModal} onClose={() => setShowPwModal(false)} />
      <AvatarCropModal open={showCropModal} src={cropSrc} onCancel={handleCropCancel} onSave={handleCropSave} />
      <ChangeDisplayNameModal
        open={showDisplayNameModal}
        currentDisplayName={accountDisplayName}
        onClose={() => setShowDisplayNameModal(false)}
        onSave={handleDisplayNameSave}
      />
    </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
export { SettingsPage };

// Dev self-tests
(function runDevTests() {
  const shouldRun = typeof window !== "undefined" && !((window as unknown as { __SETTINGS_TESTS_RAN__?: boolean }).__SETTINGS_TESTS_RAN__);
  if (!shouldRun) return;
  (window as unknown as { __SETTINGS_TESTS_RAN__?: boolean }).__SETTINGS_TESTS_RAN__ = true;
  const results: Array<{ name: string; pass: boolean; details?: string }> = [];
  const vb = makeViewBox(TAB_GEOM.width, TAB_GEOM.height);
  results.push({ name: "viewBox string format", pass: vb === `0 0 ${TAB_GEOM.width} ${TAB_GEOM.height}` });
  results.push({ name: "Geometry constants", pass: TAB_GEOM.width === 190 && TAB_GEOM.height === 42 && TAB_GEOM.overlap === 45 });
  const initialsCases: Array<[string, string]> = [["Wacek Marszalek", "WM"], ["Ada", "AD"], ["alan turing", "AT"], ["john.doe", "JD"], ["  multiple   spaces  name  ", "MN"], ["", "U"], ["x y z", "XZ"]];
  initialsCases.forEach(([name, exp]) => { const got = getInitials(name); results.push({ name: `initials(${name})`, pass: got === exp, details: got }); });
  try { console.table(results.map(r => ({ Test: r.name, Pass: r.pass, Details: r.details || "" }))); } catch {}
})();
